import { ApprDecisionDialog } from '@/common/appr/ApprDecisionDialog';
import { ApprNoteDialog } from '@/common/appr/ApprNoteDialog';
import { Banner } from '@/common/banner/Banner';
import { useKrCardContext } from '@/common/card/KrCardContext';
import { useDialog } from '@/common/dialog/DialogContext';
import { useInDialog } from '@/common/dialog/DialogScopeContext';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTab } from '../entityTabs/TabContext';
import { useEntityMgr } from '../entityView/EntityMgrContext';

/**
 * TODO: Explore posibility of the Banner solely being driven by active Jobs and not statuses on the entityObj
 */
export const EntityBanner = () => {
  const { entityDef, entityObj, jobs, hist, asOfDate } = useEntityMgr();
  const { principal } = usePrincipal();
  const { openDialog } = useDialog();
  const inDialog = useInDialog();
  const inKrCard = Boolean(useKrCardContext());
  const { tabId } = useTab();

  const rsrc = useRsrc();
  const entity = useEntityFromRoute();

  const [apprEvent, setApprEvent] = useState();
  const [apprRejectionNotif, setApprRejectionNotif] = useState();

  const shouldDisplay = !(inDialog || inKrCard || tabId);

  useEffect(() => {
    if (!shouldDisplay || Object.keys(entityObj).length === 0) return;
    const rowsReq = {
      replaceParams: {
        username: principal.username,
      },
      expr: ExprUtils.whereParams('entity', entity, 'entityId', entityObj.id),
    };

    rsrc.rows('skdr', 'APPR_EVENTS', rowsReq).then((apprEvent) => {
      setApprEvent(apprEvent[0]);
    });

    rsrc.rows('skdr', 'APPR_REJECTION_NOTIFS', rowsReq).then((apprRejectionNotifs) => {
      setApprRejectionNotif(apprRejectionNotifs[0]);
    });
  }, [shouldDisplay, entityObj?.id]);

  const openApprDecisionDialog = () => {
    const data = { runIds: [apprEvent.runId], title: 'Approving ' + entityDef.displayName };
    openDialog(ApprDecisionDialog, { data }).then((res) => {
      if (res?.type) {
        setApprEvent(null);
      }
    });
  };

  const openApprRejectionNoteDialog = () => {
    const data = {
      title: 'Rejection Note',
      subtitle: entityDef.displayName,
      note: JSON.parse(apprRejectionNotif.data).comment,
    };
    openDialog(ApprNoteDialog, { data });
  };

  const ackApprRejection = () => {
    rsrc.save('skdr', 'NOTIF', [{ id: apprRejectionNotif.id, ack: true }]).then(() => {
      setApprRejectionNotif(null);
    });
  };

  if (!shouldDisplay) return;

  return (
    <>
      {/* Approval Routes */}
      {!(hist || asOfDate) && entityObj.status === 'APPROVING' && (
        <Banner icon={fas.faExclamationTriangle} text={`Item is pending ${apprEvent ? 'your ' : ''}approval`}>
          {apprEvent && (
            <Button color="inherit" onClick={() => openApprDecisionDialog()}>
              Approve or Reject
            </Button>
          )}
        </Banner>
      )}

      {/* Approval Rejections awaiting the requester's acknowledgement */}
      {!(hist || asOfDate) && apprRejectionNotif && (
        <Banner icon={fas.faCircleXmark} text="Your approval request was rejected" color="warning">
          <Button color="inherit" onClick={() => openApprRejectionNoteDialog()}>
            View Note
          </Button>
          <Button color="inherit" onClick={() => ackApprRejection()}>
            Acknowledge
          </Button>
        </Banner>
      )}

      {/* Validation Errors */}
      {!(hist || asOfDate) &&
        entityObj.status === 'DRAFT' &&
        entityObj.valStatus === 'ERROR' &&
        !jobs.some((job) => job.cat === 'VAL') && (
          <Banner
            icon={fas.faExclamationTriangle}
            text="Errors were found while validating. Please review the Validations Tab"
            color="warning"
          />
        )}

      {/* Active Jobs */}
      {jobs.map((job) => (
        <Banner
          key={job.id}
          icon={fas.faExclamationTriangle}
          text={'Running ' + (job.cat === 'EXPORT' ? 'Export ' : '') + job.name}
        />
      ))}
    </>
  );
};
